import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { statusBadge } from "@/components/coverage/coverageUi";
import { AlertTriangle, RefreshCw, Shield } from "lucide-react";

function readinessBadge(val) {
  if (val === "ready") return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Ready</Badge>;
  if (val === "blocked") return <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30">Blocked</Badge>;
  return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Attention</Badge>;
}

function formatAppt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dt;
  }
}

export default function CoverageQueue({
  patients,
  loading,
  error,
  forbidden,
  source,
  tenant,
  onRefresh,
  onOpen,
  onVerify,
  verifyingId,
}) {
  const readyCount = patients.filter((p) => p.visit_readiness === "ready").length;
  const attentionCount = patients.filter((p) => p.visit_readiness === "attention_required").length;
  const blockedCount = patients.filter((p) => p.visit_readiness === "blocked").length;
  const reviewCount = patients.filter((p) => p.review_id).length;

  return (
    <div className="space-y-6" data-testid="coverage-queue">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-2">
            <Shield className="w-8 h-8 text-emerald-400" />
            Coverage Intelligence
          </h1>
          <p className="text-slate-400 mt-1">
            Visit readiness for the mapped practice, via Priorath BFF (not PayerMesh).
          </p>
          {tenant && (
            <p className="text-[11px] text-slate-500 mt-2 font-mono">
              tenant {tenant.tenant_id} · {tenant.practice_id} · {tenant.location_id}
              {source ? ` · source ${source}` : ""}
            </p>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="border-slate-800 text-slate-300 hover:bg-slate-800"
          onClick={onRefresh}
          disabled={loading}
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh queue
        </Button>
      </div>

      {forbidden && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-amber-200 text-sm flex gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <div>
            <p className="font-semibold">Organization is not on the Coverage allow-list</p>
            <p className="text-amber-200/80 mt-1">
              {typeof error === "string" ? error : "Only mapped customers can load Coverage Intelligence."}
            </p>
          </div>
        </div>
      )}

      {!forbidden && error && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-rose-200 text-sm">{String(error)}</div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-xl">
          <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Scheduled today</span>
          <span className="text-2xl font-bold text-white mt-2 block">{patients.length}</span>
        </div>
        <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-xl">
          <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Ready</span>
          <span className="text-2xl font-bold text-emerald-400 mt-2 block">{readyCount}</span>
        </div>
        <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-xl">
          <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Needs attention</span>
          <span className="text-2xl font-bold text-amber-400 mt-2 block">{attentionCount}</span>
        </div>
        <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-xl">
          <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Needs review</span>
          <span className="text-2xl font-bold text-rose-400 mt-2 block">{reviewCount || blockedCount}</span>
        </div>
      </div>

      <div className="bg-slate-950/40 border border-slate-800 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="text-slate-400">Patient</TableHead>
              <TableHead className="text-slate-400">Appointment</TableHead>
              <TableHead className="text-slate-400">Readiness</TableHead>
              <TableHead className="text-slate-400">Eligibility</TableHead>
              <TableHead className="text-slate-400">Coverage CI</TableHead>
              <TableHead className="text-slate-400">Next action</TableHead>
              <TableHead className="text-slate-400 text-right"> </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-slate-500 text-center py-10">
                  Loading coverage readiness…
                </TableCell>
              </TableRow>
            )}
            {!loading && patients.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-slate-500 text-center py-10">
                  No scheduled coverage patients for this mapped practice.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              patients.map((pat) => (
                <TableRow key={pat.patient_id} className="border-slate-800 hover:bg-slate-900/60">
                  <TableCell>
                    <div className="text-white font-medium">
                      {pat.first_name} {pat.last_name}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono">id {pat.patient_id}</div>
                  </TableCell>
                  <TableCell className="text-slate-300 text-sm">
                    {formatAppt(pat.appointment_datetime)}
                    <div className="text-[11px] text-slate-500">{pat.appointment_status || ""}</div>
                  </TableCell>
                  <TableCell>{readinessBadge(pat.visit_readiness)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {statusBadge(pat.eligibility_status)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 items-center">
                      {statusBadge(pat.coverage_intelligence_status)}
                      {pat.payer_knowledge_status ? (
                        <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                          {pat.payer_knowledge_status === "AVAILABLE" ? "Dental policy" : "No lakehouse"}
                        </Badge>
                      ) : null}
                      {pat.has_ortho ? (
                        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Ortho</Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300 text-xs">{pat.next_best_action || "—"}</TableCell>
                  <TableCell className="text-right space-x-2 whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-700 text-slate-200"
                      onClick={() => onOpen(pat)}
                    >
                      Open
                    </Button>
                    <Button
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                      disabled={verifyingId === pat.patient_id}
                      onClick={() => onVerify(pat)}
                    >
                      Verify
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
